import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Button, createStyles, Group, Menu, SegmentedControl, Text} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ReviewSummary} from 'constants/SourceConstants';
import {SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import strUtil from 'util/strUtil';

import {HomepageAssignedProcessTasksQuery} from '../__generated/HomepageAssignedProcessTasksQuery';
import CardSection from '../CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT} from '../HomepageConstants';
import EntriesRows from './EntriesRows';
import {
  MyWorkCategory,
  REVIEW_ITEM_STATUS_LABELS,
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_LABELS,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
} from './MyWorkConstants';
import {
  getSelectAllMenuIcon,
  getStatusMenuButtonLabel,
  getStatusSelectionState,
  toggleAllStatusSelections,
  toggleStatusSelection,
} from './MyWorkStatusFilter';
import {MyWorkStatusMenuItem} from './MyWorkStatusMenuItem';
import {
  getCategoryCounts,
  getSelectedReviewSummaryIds,
  getSelectedTaskIds,
  makeTasksReviewSummary,
} from './MyWorkUtils';
import {openAllAssignedTasksSearch, openMyWorkReviewableSearch} from './openMyWorkSearch';
import TasksRows from './TasksRows';
import WorksheetsRows from './WorksheetsRows';

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

/** Left-to-right tab order in the My Work segmented control. */
const MY_WORK_CATEGORY_PRECEDENCE = [MyWorkCategory.ENTRIES, MyWorkCategory.WORKSHEETS, MyWorkCategory.TASKS];

function getLeftmostEnabledCategory(categories: Array<MyWorkCategory>): MyWorkCategory {
  return MY_WORK_CATEGORY_PRECEDENCE.find((category) => categories.includes(category))!;
}

const useMyWorkMenuIconStyles = createStyles((theme, {active}: {active: boolean}) => ({
  icon: {
    '&&': {
      width: '16px',
      height: '16px',
    },
    color: active ? theme.colors.blue[6] : theme.colors.gray[3],
  },
}));

const useMyWorkContentStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.md,
  },
  flexGrow: {
    '&&': {
      flexGrow: 1,
    },
  },
}));

const useCategoryLabelStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  badge: {
    backgroundColor: selected ? theme.white : theme.colors.gray[1],
  },
}));

function CategoryLabel({
  category,
  count,
  selected,
}: {
  category: MyWorkCategory;
  count: number;
  selected: boolean;
}): JSX.Element {
  const {classes} = useCategoryLabelStyles({selected});

  return (
    <Group noWrap={true} spacing="xs">
      {strUtil.toSentenceCase(category.toString())}
      <Badge size="xs" radius="lg" color={selected ? 'white' : 'gray'} className={classes.badge}>
        {count}
      </Badge>
    </Group>
  );
}

interface MyWorkSectionContentProps {
  entryReviewSummary: ReviewSummary | null;
  worksheetReviewSummary: ReviewSummary | null;
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageAssignedProcessTasksQuery>;
  enabledCategories: Array<MyWorkCategory>;
}

/**
 * Renders a table displaying the current user's entries, worksheets, or tasks and their status.
 * Also renders a status filter and a toggle to select which of the three entity types is being viewed.
 */
export default function MyWorkSectionContent({
  entryReviewSummary,
  worksheetReviewSummary,
  tasksQueryResult,
  enabledCategories,
}: MyWorkSectionContentProps): JSX.Element {
  const [selectedCategory, setSelectedCategory] = useState(() =>
    getLeftmostEnabledCategory(enabledCategories)
  );
  const [selectedEntryStatuses, setSelectedEntryStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedWorksheetStatuses, setSelectedWorksheetStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedTaskStatuses, setSelectedTaskStatuses] = useState(TASK_STATUS_LABELS);
  const {classes} = useMyWorkContentStyles();

  const tasksReviewSummary = useMemo(() => makeTasksReviewSummary(tasksQueryResult), [tasksQueryResult]);

  const statusSelection = getStatusSelectionState(
    selectedCategory === MyWorkCategory.TASKS
      ? selectedTaskStatuses
      : selectedCategory === MyWorkCategory.WORKSHEETS
        ? selectedWorksheetStatuses
        : selectedEntryStatuses,
    selectedCategory === MyWorkCategory.TASKS ? TASK_STATUS_LABELS : REVIEW_ITEM_STATUS_LABELS
  );
  const {classes: menuIconClasses} = useMyWorkMenuIconStyles({active: statusSelection !== 'none'});

  const categoryUi = {
    [MyWorkCategory.ENTRIES]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedEntryStatuses,
      setSelectedStatuses: setSelectedEntryStatuses,
    },
    [MyWorkCategory.WORKSHEETS]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedWorksheetStatuses,
      setSelectedStatuses: setSelectedWorksheetStatuses,
    },
    [MyWorkCategory.TASKS]: {
      statusLabels: TASK_STATUS_LABELS,
      selectedStatuses: selectedTaskStatuses,
      setSelectedStatuses: setSelectedTaskStatuses,
    },
  }[selectedCategory];

  const {entriesCount, worksheetsCount, tasksCount} = getCategoryCounts({
    entryReviewSummary,
    worksheetReviewSummary,
    tasksQueryResult,
    showEntriesCategory: enabledCategories.includes(MyWorkCategory.ENTRIES),
    showWorksheetsCategory: enabledCategories.includes(MyWorkCategory.WORKSHEETS),
    showTasksCategory: enabledCategories.includes(MyWorkCategory.TASKS),
  });

  const toDoCategoryLabels: Record<MyWorkCategory, React.ReactNode> = {
    [MyWorkCategory.ENTRIES]: (
      <CategoryLabel
        category={MyWorkCategory.ENTRIES}
        count={entriesCount}
        selected={selectedCategory === MyWorkCategory.ENTRIES && enabledCategories.length > 1}
      />
    ),
    [MyWorkCategory.WORKSHEETS]: (
      <CategoryLabel
        category={MyWorkCategory.WORKSHEETS}
        count={worksheetsCount}
        selected={selectedCategory === MyWorkCategory.WORKSHEETS && enabledCategories.length > 1}
      />
    ),
    [MyWorkCategory.TASKS]: (
      <CategoryLabel
        category={MyWorkCategory.TASKS}
        count={tasksCount}
        selected={selectedCategory === MyWorkCategory.TASKS && enabledCategories.length > 1}
      />
    ),
  };

  const statusMenuLabel = getStatusMenuButtonLabel(categoryUi.selectedStatuses, statusSelection);
  const isTaskCategory = selectedCategory === MyWorkCategory.TASKS;
  const reviewStatusCounts =
    selectedCategory === MyWorkCategory.ENTRIES ? entryReviewSummary : worksheetReviewSummary;

  return (
    <CardSection title="My work" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      <Group spacing="xs" className={classes.filterRow}>
        {/* If there's only one category, don't show the segmented control */}
        {enabledCategories.length === 1 ? (
          <Text size="lg" ml="sm">
            {toDoCategoryLabels[enabledCategories[0]]}
          </Text>
        ) : (
          <SegmentedControl
            aria-label="Select category"
            variant="contained"
            data={enabledCategories.map((category) => ({
              value: category,
              label: toDoCategoryLabels[category],
            }))}
            value={selectedCategory.toString()}
            onChange={(value) => {
              setSelectedCategory(value as MyWorkCategory);
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'categoryControl'})}
          />
        )}
        <Group position="apart" noWrap={true} className={classes.flexGrow}>
          <Menu position="bottom-start" width="300px" withinPortal={true} closeOnItemClick={false}>
            <Menu.Target>
              <Button
                // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design (yet)
                variant="default"
                size="md"
                radius="md"
                ml="xs"
                rightIcon={<BenchlingIcon icon="chevron-down" />}
                {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuButton'})}
              >
                {statusMenuLabel}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                key="all"
                icon={
                  <BenchlingIcon
                    icon={getSelectAllMenuIcon(statusSelection)}
                    className={menuIconClasses.icon}
                    aria-hidden={true}
                  />
                }
                {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuItem', key: 'toggleAll'})}
                onClick={() => {
                  categoryUi.setSelectedStatuses(
                    toggleAllStatusSelections(categoryUi.selectedStatuses, categoryUi.statusLabels)
                  );
                }}
              >
                Select all
              </Menu.Item>
              <Menu.Divider />
              {categoryUi.statusLabels.map((statusLabel) => {
                const sharedProps = {
                  key: statusLabel,
                  isSelected: categoryUi.selectedStatuses.includes(statusLabel),
                  setIsSelected: (selected: boolean) => {
                    categoryUi.setSelectedStatuses(
                      toggleStatusSelection(categoryUi.selectedStatuses, statusLabel, selected)
                    );
                  },
                  statusLabel,
                  dataTestAttrs: {
                    component: TEST_COMPONENT,
                    element: 'statusMenuItem' as const,
                    key: statusLabel,
                  },
                };
                const statusMenuItemProps = isTaskCategory
                  ? {
                      isTask: true as const,
                      statusCounts: tasksReviewSummary,
                      summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel],
                    }
                  : {
                      isTask: false as const,
                      statusCounts: reviewStatusCounts,
                      summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel],
                    };
                return <MyWorkStatusMenuItem {...sharedProps} {...statusMenuItemProps} />;
              })}
            </Menu.Dropdown>
          </Menu>
          <Button
            size="md"
            radius="md"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design (yet)
            variant="default"
            color="gray"
            onClick={() => {
              trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_MY_WORK_SEE_ALL_BUTTON);
              if (selectedCategory === MyWorkCategory.ENTRIES) {
                openMyWorkReviewableSearch([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY]);
              } else if (selectedCategory === MyWorkCategory.WORKSHEETS) {
                openMyWorkReviewableSearch([ModelTypeConstants.WORKSHEET]);
              } else if (selectedCategory === MyWorkCategory.TASKS) {
                openAllAssignedTasksSearch();
              }
            }}
          >
            See all {selectedCategory}
          </Button>
        </Group>
      </Group>
      <div>
        {selectedCategory === MyWorkCategory.ENTRIES && (
          <EntriesRows
            selectedEntryIds={getSelectedReviewSummaryIds(entryReviewSummary, selectedEntryStatuses)}
          />
        )}
        {selectedCategory === MyWorkCategory.WORKSHEETS && (
          <WorksheetsRows
            selectedWorksheetIds={getSelectedReviewSummaryIds(
              worksheetReviewSummary,
              selectedWorksheetStatuses
            )}
          />
        )}
        {selectedCategory === MyWorkCategory.TASKS && (
          <TasksRows selectedTaskIds={getSelectedTaskIds(tasksReviewSummary, selectedTaskStatuses)} />
        )}
      </div>
    </CardSection>
  );
}
